(() => {
  if (window.cartDrawerDiscount) return;

  const cartUrl = window.theme?.routes?.cart_url || '/cart';

  const selectors = {
    root: '[data-cart-discount]',
    form: '[data-cart-discount-form]',
    input: '[data-cart-discount-input]',
    submit: '[data-cart-discount-submit]',
    message: '[data-cart-discount-message]',
    remove: '[data-discount-remove]',
    summary: '[data-cart-summary]',
    originalTotal: '[data-cart-original-total]',
    checkoutContent: '[data-cart-checkout-content]',
    checkoutLabel: '.cart__checkout-label',
    checkoutPrices: '.cart__checkout-prices',
    shippingMessage: '[data-cart-message]',
    shippingMessageDefault: '[data-message-default]',
    shippingProgress: '[data-cart-message-progress]',
    subscriptionCheckbox: '.juo-subscribe-cart__selling-plans-checkbox',
    subscriptionSwitcher: '.juo-subscribe-switcher',
    subscriptionSelect:
      '.juo-subscribe-cart__selling-plan-switcher--select-container, .juo-subscribe-cart__selling-plan-switcher--select',
    subscriptionPlanOption: '[data-cart-drawer] .juo-subscribe-cart__selling-plan-switcher--select option',
    cartItem: '[data-cart-item]',
    customCode: '[data-cart-drawer-custom-code]',
    customCodeSlot: '[data-cart-drawer-custom-code-slot]',
    toggle: '[data-cart-discount-toggle]',
    toggleText: '[data-cart-discount-toggle-text]',
    termsCheckbox: '[data-cart-drawer] [data-cart-acceptance-checkbox]',
    checkoutButtons: '[data-cart-drawer] [data-cart-checkout-buttons]',
    checkoutButton: '[data-cart-drawer] .cart__checkout',
  };

  const subscribedLabel = 'Subskrypcja';
  const compactPlanNames = window.matchMedia('(max-width: 767px)');

  // Storefront API (bez tokenu dostępu) zmienia ten sam koszyk co Ajax API, ale przy kodzie, który nie działa,
  // podaje powód (CartWarning) – Ajax API zwraca tylko applicable: false.
  // Wersję API podbijaj raz w roku: Shopify wspiera każdą przez ok. 12 miesięcy.
  const storefrontApiUrl = '/api/2026-10/graphql.json';
  const discountCodesMutation = `
    mutation CartDrawerDiscountCodes($cartId: ID!, $codes: [String!]!) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $codes) {
        cart {
          discountCodes { code applicable }
          discountAllocations { ... on CartCodeDiscountAllocation { code } }
          lines(first: 100) {
            nodes { discountAllocations { ... on CartCodeDiscountAllocation { code } } }
          }
        }
        warnings { code message }
        userErrors { code message }
      }
    }
  `;

  // Powód odrzucenia kodu → komunikat z data-msg-* w snippets/cart-drawer-discount.liquid
  const warningMessages = {
    DISCOUNT_NOT_FOUND: 'msgNotFound',
    DISCOUNT_CURRENTLY_INACTIVE: 'msgInactive',
    DISCOUNT_USAGE_LIMIT_REACHED: 'msgUsageLimit',
    DISCOUNT_CUSTOMER_USAGE_LIMIT_REACHED: 'msgCustomerUsageLimit',
    DISCOUNT_CUSTOMER_NOT_ELIGIBLE: 'msgCustomerNotEligible',
    DISCOUNT_PURCHASE_NOT_IN_RANGE: 'msgPurchaseNotInRange',
    DISCOUNT_QUANTITY_NOT_IN_RANGE: 'msgQuantityNotInRange',
    DISCOUNT_NO_ENTITLED_LINE_ITEMS: 'msgNoEntitledItems',
  };

  // Kody, które sprawdza dopiero kasa – zostają w koszyku z informacją zamiast błędu: na darmową dostawę
  // (w koszyku nie ma jeszcze wybranej dostawy) i dla wybranych klientów (klient loguje się w kasie).
  const checkoutPendingWarnings = {
    DISCOUNT_NO_ENTITLED_SHIPPING_LINES: 'msgShippingPending',
    DISCOUNT_ELIGIBLE_CUSTOMER_MISSING: 'msgCheckoutPending',
  };

  let keepMessage = false;
  let messageTimer = null;

  const same = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

  // „Zastosowano największą zniżkę”: zapamiętujemy (na czas sesji) kod, który wygrał z innym kodem, z którym się nie
  // łączy – koszyk Shopify zostawia wtedy korzystniejszy. Notka trzyma się tego kodu, dopóki jest w koszyku.
  const bestDiscountKey = 'cartDrawerBestDiscount';
  const readBestDiscount = () => {
    try {
      return JSON.parse(sessionStorage.getItem(bestDiscountKey) || '[]');
    } catch (error) {
      return [];
    }
  };
  const writeBestDiscount = (codes) => {
    try {
      codes.length ? sessionStorage.setItem(bestDiscountKey, JSON.stringify(codes)) : sessionStorage.removeItem(bestDiscountKey);
    } catch (error) {}
  };

  const request = async (path, body) => {
    const response = await fetch(path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

    return response.json();
  };

  const getCart = () => request(`${cartUrl}.js`);

  const allocatedCodesFromAjax = (cart) => {
    const lineApplications = (cart.items || []).flatMap((item) =>
      (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application)
    );

    return [...(cart.cart_level_discount_applications || []), ...lineApplications]
      .filter((application) => application?.type === 'discount_code')
      .map((application) => application.title);
  };

  // Zwraca { discount_codes, allocatedCodes, warnings }. Gdy Storefront API nie odpowie, zapisuje kody
  // przez Ajax API – wtedy warnings === null (powód odrzucenia nieznany, komunikat ogólny).
  const setCodes = async (cart, codes) => {
    try {
      const response = await fetch(storefrontApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          query: discountCodesMutation,
          variables: { cartId: `gid://shopify/Cart/${cart.token}`, codes },
        }),
      });
      const { data, errors } = await response.json();
      const payload = data?.cartDiscountCodesUpdate;

      if (!response.ok || errors?.length || !payload?.cart || payload.userErrors.length) {
        throw new Error(`Storefront API: ${JSON.stringify(errors || payload?.userErrors || response.status)}`);
      }

      const allocations = [
        ...payload.cart.discountAllocations,
        ...payload.cart.lines.nodes.flatMap((line) => line.discountAllocations),
      ];

      return {
        discount_codes: payload.cart.discountCodes,
        allocatedCodes: allocations.map((allocation) => allocation.code).filter(Boolean),
        warnings: payload.warnings.filter((warning) => warning.code.startsWith('DISCOUNT_')),
      };
    } catch (error) {
      console.warn(error);

      const ajaxCart = await request(`${cartUrl}/update.js`, { discount: codes.join(',') });

      return {
        discount_codes: ajaxCart.discount_codes || [],
        allocatedCodes: allocatedCodesFromAjax(ajaxCart),
        warnings: null,
      };
    }
  };

  const allCodes = (cart) => (cart.discount_codes || []).map((entry) => entry.code);

  const activeCodes = (cart) =>
    (cart.discount_codes || []).filter((entry) => entry.applicable).map((entry) => entry.code);

  const giftCampaignCodes = () =>
    (window.AVADA_FREE_GIFTS?.giftCampaigns || []).map((campaign) => campaign.code).filter(Boolean);

  const isAllocated = (result, code) => result.allocatedCodes.some((allocated) => same(allocated, code));

  const cartHasSubscription = () => document.querySelector(selectors.summary)?.dataset.hasSubscription === 'true';

  // Ostrzeżenia dotyczą całego koszyka. Jeśli był w nim już niedziałający kod (np. z Avady), jego ostrzeżenia
  // są też w odpowiedzi po cofnięciu zmian – odejmujemy je, żeby komunikat dotyczył wpisanego kodu.
  const freshWarning = (after, reverted) => {
    const remaining = [...(reverted || [])].map((warning) => warning.code);

    return (after || []).find((warning) => {
      const index = remaining.indexOf(warning.code);
      if (index === -1) return true;
      remaining.splice(index, 1);
      return false;
    });
  };

  const warningForNewCode = (after, reverted) => (after ? freshWarning(after, reverted) || after[0] || null : null);

  // Subskrypcja ma zawsze darmową dostawę, a motyw liczy pasek tylko z kwoty koszyka –
  // po każdym jego przeliczeniu przełączamy komunikat na sukces.
  const syncShippingMessage = (summary) => {
    if (summary.dataset.hasSubscription !== 'true') return;

    document.querySelectorAll(selectors.shippingMessage).forEach((message) => {
      if (message.dataset.cartMessage !== 'true') return;

      message.classList.add('is-success');
      message.classList.remove('is-hidden');
      message.querySelector(selectors.shippingMessageDefault)?.classList.add('is-hidden');
      message.querySelectorAll(selectors.shippingProgress).forEach((progress) => progress.classList.add('is-hidden'));
    });
  };

  // Po zaznaczeniu subskrypcji krótszy tekst niż oferta Juo („Subskrybuj i oszczędź …”),
  // żeby w tej samej linii zmieściły się lista częstotliwości dostaw i ilość.
  const syncSubscriptionLabel = (checkbox) => {
    const text = checkbox.closest('label')?.querySelector('span');
    if (!text) return;

    text.dataset.offerText ??= text.textContent.trim();
    text.textContent = checkbox.checked ? subscribedLabel : text.dataset.offerText;
  };

  // Juo zmienia pozycję na serwerze 1–3 s (koszyk → zmiana pozycji → przerysowanie szuflady). Nie czekamy:
  // lista częstotliwości pokazuje się / znika od razu, a cena jest przygaszona do przerysowania szuflady.
  const showSubscriptionPending = (checkbox) => {
    checkbox
      .closest(selectors.subscriptionSwitcher)
      ?.querySelectorAll(selectors.subscriptionSelect)
      .forEach((element) => element.classList.toggle('hidden', !checkbox.checked));
    checkbox.closest(selectors.cartItem)?.classList.add('is-subscription-pending');
  };

  // Nazwy planów skracamy tylko w szufladzie (na stronie produktu i w kasie zostają pełne):
  // dopisek w nawiasie zawsze („(rekomendujemy)” nie mieści się nawet na desktopie),
  // a na telefonie także „Dostawa co” – na 360 px lista ma ok. 80 px na tekst.
  const syncSubscriptionControls = () => {
    document.querySelectorAll(selectors.subscriptionCheckbox).forEach(syncSubscriptionLabel);
    document.querySelectorAll(selectors.subscriptionPlanOption).forEach((option) => {
      option.dataset.planName ??= option.textContent.replace(/\s*\([^)]*\)\s*$/, '').trim();
      option.textContent = compactPlanNames.matches
        ? option.dataset.planName.replace(/^dostawa\s+co\s+/i, 'Co ')
        : option.dataset.planName;
    });
  };

  // Przekreślona cena w przycisku kasy tylko, gdy cała treść mieści się w jednej linii. Na wąskich telefonach,
  // przy wyższych kwotach i w Safari (szerzej rysowany font) zostaje sama cena po rabacie – rabat widać w „Zniżki:”.
  // Gdy nawet wtedy coś wystaje, napis dostaje ciaśniejszy krój (is-tight).
  const fitCheckoutPrices = () => {
    const content = document.querySelector(selectors.checkoutContent);
    const label = content?.querySelector(selectors.checkoutLabel);
    const prices = content?.querySelector(selectors.checkoutPrices);
    if (!content || !label || !prices || !content.clientWidth) return;

    const original = content.querySelector(selectors.originalTotal);
    const overflows = () => {
      const box = content.getBoundingClientRect();
      const right = Math.max(label.getBoundingClientRect().right, prices.getBoundingClientRect().right);
      return prices.offsetTop > label.offsetTop || right > box.right + 1 || content.scrollWidth > content.clientWidth + 1;
    };

    content.classList.remove('is-tight');
    original?.classList.remove('is-dropped');
    if (original && !original.hidden && overflows()) original.classList.add('is-dropped');
    if (overflows()) content.classList.add('is-tight');
  };

  // Mierzymy ponownie, gdy zmienia się szerokość przycisku (otwarcie szuflady, obrót telefonu) albo napisu i kwot
  // (załadowanie fontu, nowa suma po kodzie – także gdy motyw lub Juo podmieni ją po naszym pomiarze)
  const checkoutSizes = new WeakMap();
  const checkoutResize = new ResizeObserver((entries) => {
    let changed = false;
    entries.forEach((entry) => {
      const width = Math.round(entry.contentRect.width);
      if (checkoutSizes.get(entry.target) !== width) {
        checkoutSizes.set(entry.target, width);
        changed = true;
      }
    });
    if (changed) requestAnimationFrame(fitCheckoutPrices);
  });
  // Zmiana tekstu kwot (niezależnie od ResizeObserver – działa w każdej przeglądarce); klasy is-dropped/is-tight
  // nie wywołują pomiaru ponownie, bo patrzymy tylko na tekst i atrybut hidden
  const checkoutMutations = new MutationObserver(() => setTimeout(fitCheckoutPrices));

  const watchCheckout = () => {
    const content = document.querySelector(selectors.checkoutContent);
    if (content && !content.dataset.watched) {
      content.dataset.watched = 'true';
      checkoutResize.observe(content);
      content.querySelectorAll(`${selectors.checkoutLabel}, ${selectors.checkoutPrices}`).forEach((el) => checkoutResize.observe(el));
      checkoutMutations.observe(content, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
    }
  };

  document.fonts?.ready.then(() => fitCheckoutPrices());
  document.fonts?.addEventListener?.('loadingdone', () => fitCheckoutPrices());

  // Notka pod kodem w „Zniżki:” – tylko gdy zapamiętany kod nadal jest w koszyku (inaczej zapomnij)
  const syncBestDiscount = (summary) => {
    if (!summary) return;
    summary.querySelectorAll('[data-best-discount-note]').forEach((note) => note.remove());

    // Kody po nazwie w znaczniku (kod kampanii prezentowej nie ma przycisku usuwania)
    const pills = [...summary.querySelectorAll('.cart-summary__code')].map((pill) => ({
      pill,
      code: pill.querySelector('span')?.textContent.trim() || '',
    }));
    const remembered = readBestDiscount();
    const kept = remembered.filter((code) => pills.some((entry) => same(entry.code, code)));
    if (kept.length !== remembered.length) writeBestDiscount(kept);

    pills
      .filter((entry) => kept.some((code) => same(entry.code, code)))
      .forEach(({ pill }) => {
        const note = document.createElement('span');
        note.className = 'cart-summary__best';
        note.dataset.bestDiscountNote = '';
        note.textContent = summary.dataset.bestLabel || '';
        pill.closest('dt')?.append(note);
      });
  };

  const syncFromSummary = () => {
    const summary = document.querySelector(selectors.summary);
    if (!summary) return;

    syncShippingMessage(summary);
    syncBestDiscount(summary);

    const original = document.querySelector(selectors.originalTotal);
    if (original) {
      original.textContent = summary.dataset.originalTotal || '';
      original.hidden = summary.dataset.hasDiscount !== 'true';
      watchCheckout();
      fitCheckoutPrices();
    }

    const root = document.querySelector(selectors.root);
    const input = root?.querySelector(selectors.input);
    const placeholder = Number(summary.dataset.codeCount) > 0 ? root?.dataset.placeholderMore : root?.dataset.placeholderEmpty;
    if (input) input.placeholder = placeholder;

    const toggleText = root?.querySelector(selectors.toggleText);
    if (toggleText && placeholder) toggleText.textContent = placeholder;
  };

  // Pole kodu jest domyślnie zamknięte – link „Masz kod rabatowy?” je otwiera (i znika). Stan zostaje do przeładowania
  // strony, bo stopka szuflady nie jest przerysowywana przy zmianach koszyka.
  const openDiscount = (root) => {
    const form = root?.querySelector(selectors.form);
    const toggle = root?.querySelector(selectors.toggle);
    if (!form || !form.hidden) return;

    form.hidden = false;
    if (toggle) {
      toggle.hidden = true;
      toggle.setAttribute('aria-expanded', 'true');
    }
    form.querySelector(selectors.input)?.focus();
  };

  // Przycisk kasy jest wyszarzony do zaznaczenia zgody (CSS) – tu ten sam stan dla czytników ekranu
  const syncTerms = () => {
    const checkbox = document.querySelector(selectors.termsCheckbox);
    const button = document.querySelector(selectors.checkoutButton);
    if (!checkbox || !button) return;

    button.setAttribute('aria-disabled', String(!checkbox.checked));
    if (checkbox.checked) checkbox.classList.remove('is-attention');
  };

  // Kliknięcie nieaktywnego przycisku: motyw pokazuje komunikat, a my zapalamy ramkę przy zgodzie (do zaznaczenia)
  const highlightTerms = () => {
    const checkbox = document.querySelector(selectors.termsCheckbox);
    if (!checkbox || checkbox.checked) return;

    checkbox.classList.add('is-attention');
  };

  // Własny kod z ustawień (np. licznik) renderuje się poza szablonem szuflady – po jej wyrenderowaniu
  // przenosimy go na górę. Przeniesienie zachowuje stan (działający licznik, podpięte zdarzenia).
  const placeCustomCode = () => {
    const holder = document.querySelector(selectors.customCode);
    const slot = document.querySelector(selectors.customCodeSlot);
    if (!holder || !slot || holder.parentElement === slot) return;

    slot.append(holder);
    holder.removeAttribute('aria-hidden');
    holder.classList.add('is-placed');
  };

  const lockGiftCampaignCodes = () => {
    const locked = giftCampaignCodes();
    if (!locked.length) return;

    document.querySelectorAll(selectors.remove).forEach((button) => {
      if (locked.some((code) => same(code, button.dataset.discountRemove))) button.remove();
    });
  };

  const refreshDrawer = () => {
    keepMessage = true;
    document.dispatchEvent(new CustomEvent('reload-side-cart'));
  };

  const showMessage = (root, key, values = {}, state = 'info') => {
    const element = root?.querySelector(selectors.message);
    if (!element) return;

    const template = key ? root.dataset[key] || '' : '';
    const text = template.replace(/\[(\w+)\]/g, (match, name) => values[name] ?? match);

    element.textContent = text;
    element.dataset.state = state;
    element.hidden = !text;

    clearTimeout(messageTimer);
    if (text && state !== 'error') {
      messageTimer = setTimeout(() => {
        if (element.textContent === text) {
          element.textContent = '';
          element.hidden = true;
        }
      }, 6000);
    }
  };

  const setBusy = (form, busy) => {
    const submit = form.querySelector(selectors.submit);

    form.classList.toggle('is-loading', busy);
    submit.disabled = busy;
    submit.setAttribute('aria-busy', String(busy));
  };

  const markInvalid = (input) => {
    input.setAttribute('aria-invalid', 'true');
    input.select();
  };

  // reasonKnown = odpowiedziało Storefront API. Bez konkretnego powodu, przy aktywnym innym kodzie,
  // to konflikt rabatów; gdy API nie odpowiedziało – komunikat ogólny.
  const showRejected = (root, code, warning, previousActive, reasonKnown) => {
    const key = warning && warningMessages[warning.code];

    if (key) {
      showMessage(root, key, { code }, 'error');
    } else if (warning?.code === 'DISCOUNT_INCOMPATIBLE_PURCHASE_TYPE') {
      showMessage(root, cartHasSubscription() ? 'msgSubscriptionExcluded' : 'msgSubscriptionOnly', { code }, 'error');
    } else if (reasonKnown && previousActive.length) {
      // Koszyk zostawił korzystniejszy kod – to informacja, nie błąd: czyścimy pole zamiast je oznaczać
      const input = root?.querySelector(selectors.input);
      if (input) {
        input.removeAttribute('aria-invalid');
        input.value = '';
      }
      showMessage(root, 'msgNotCombined', { code, active: previousActive.join(', ') }, 'info');
      writeBestDiscount(previousActive);
      syncBestDiscount(document.querySelector(selectors.summary));
    } else {
      showMessage(root, 'msgInvalid', { code }, 'error');
    }
  };

  const apply = async (form) => {
    const root = form.closest(selectors.root);
    const input = form.querySelector(selectors.input);
    const code = input.value.trim().toUpperCase();

    input.removeAttribute('aria-invalid');

    if (!code) {
      input.setAttribute('aria-invalid', 'true');
      showMessage(root, 'msgEmpty', {}, 'error');
      input.focus();
      return;
    }

    setBusy(form, true);

    try {
      const before = await getCart();
      const previousActive = activeCodes(before);
      const existing = (before.discount_codes || []).find((entry) => same(entry.code, code));

      if (existing?.applicable) {
        input.value = '';
        showMessage(root, 'msgDuplicate', { code }, 'info');
        return;
      }

      // Kod jest już w koszyku, ale nie działa – pytamy o powód (bez zmiany kodów). Ostrzeżenia dotyczą
      // całego koszyka, więc konkretny powód pokazujemy tylko, gdy to jedyny niedziałający kod.
      if (existing) {
        const current = await setCodes(before, allCodes(before));
        const onlyInactive = allCodes(before).length - previousActive.length === 1;
        const warning = onlyInactive ? current.warnings?.[0] : null;

        if (checkoutPendingWarnings[warning?.code]) {
          input.value = '';
          showMessage(root, checkoutPendingWarnings[warning.code], { code }, 'info');
          return;
        }

        markInvalid(input);
        showRejected(root, code, warning, previousActive, false);
        return;
      }

      const cart = await setCodes(before, [...allCodes(before), code]);
      const result = cart.discount_codes.find((entry) => same(entry.code, code));

      if (!result || !result.applicable) {
        // Gdy pozostałe kody działają, jedyne ostrzeżenie dotyczy wpisanego kodu. W innym przypadku powód
        // ustalamy po cofnięciu zmian – a kod sprawdzany dopiero w kasie dodajemy z powrotem.
        const allActive = previousActive.length === allCodes(before).length;
        let pending = allActive && cart.warnings?.length === 1 ? checkoutPendingWarnings[cart.warnings[0].code] : null;

        if (!pending) {
          const reverted = await setCodes(before, allCodes(before));
          pending = checkoutPendingWarnings[freshWarning(cart.warnings, reverted.warnings)?.code];

          if (!pending) {
            markInvalid(input);
            showRejected(
              root,
              code,
              warningForNewCode(cart.warnings, reverted.warnings),
              previousActive,
              cart.warnings !== null
            );
            return;
          }

          await setCodes(before, [...allCodes(before), code]);
        }

        input.value = '';
        showMessage(root, pending, { code }, 'info');
        refreshDrawer();
        return;
      }

      const nowActive = activeCodes(cart);
      const replaced = previousActive.filter((previous) => !nowActive.some((active) => same(active, previous)));

      input.value = '';

      if (replaced.length) {
        showMessage(root, 'msgReplaced', { code, previous: replaced.join(', ') }, 'info');
        writeBestDiscount([code]);
      } else {
        showMessage(root, isAllocated(cart, code) ? 'msgSuccess' : 'msgPending', { code }, 'success');
      }

      refreshDrawer();
    } catch (error) {
      console.error(error);
      showMessage(root, 'msgError', { code }, 'error');
    } finally {
      setBusy(form, false);
    }
  };

  const remove = async (button) => {
    const code = button.dataset.discountRemove;
    const root = document.querySelector(selectors.root);
    const row = button.closest('.cart-summary__row');

    button.disabled = true;
    row?.classList.add('is-removing');

    try {
      const cart = await getCart();

      await setCodes(cart, allCodes(cart).filter((existing) => !same(existing, code)));
      showMessage(root, 'msgRemoved', { code }, 'info');
      refreshDrawer();
    } catch (error) {
      console.error(error);
      button.disabled = false;
      row?.classList.remove('is-removing');
      showMessage(root, 'msgError', { code }, 'error');
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.(selectors.form);
    if (!form) return;

    event.preventDefault();
    apply(form);
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.(selectors.remove);
    if (!button) return;

    event.preventDefault();
    remove(button);
  });

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.(selectors.toggle);
    if (toggle) {
      event.preventDefault();
      openDiscount(toggle.closest(selectors.root));
      return;
    }

    if (event.target.closest?.(selectors.checkoutButtons)) highlightTerms();
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches?.(selectors.input)) event.target.removeAttribute('aria-invalid');
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches?.(selectors.termsCheckbox)) {
      syncTerms();
      return;
    }

    if (!event.target.matches?.(selectors.subscriptionCheckbox)) return;

    syncSubscriptionLabel(event.target);
    showSubscriptionPending(event.target);
  });

  compactPlanNames.addEventListener('change', syncSubscriptionControls);

  document.addEventListener('theme:cart:loaded', () => {
    placeCustomCode();
    lockGiftCampaignCodes();
    syncFromSummary();
    syncSubscriptionControls();
    syncTerms();
  });

  document.addEventListener('theme:cart:added', () => {
    lockGiftCampaignCodes();
    syncFromSummary();
    syncSubscriptionControls();
    syncTerms();

    if (keepMessage) {
      keepMessage = false;
      return;
    }

    const root = document.querySelector(selectors.root);
    root?.querySelector(selectors.input)?.removeAttribute('aria-invalid');
    showMessage(root, null);
  });

  placeCustomCode();
  syncFromSummary();
  syncSubscriptionControls();
  syncTerms();

  window.cartDrawerDiscount = { apply, remove };
})();
